// Domain configuration for the Mono app shell.
//
// Each entry describes a top-level domain (Knowledge, Tasks, ...) and its
// sub-nav entries. DomainNav and SubNav render from this config so adding a
// new domain is a one-entry edit rather than a shell rewrite.
//
// Keep entries in render order. basePath must match the route prefix used in
// routes.jsx.

// Sections are grouped in the header (board #104): Work (the day-to-day
// tools), Labs (side projects) and the account menu under your avatar.
// Knowledge is called "Library" in the nav — its routes are unchanged.
export const NAV_GROUPS = [
  { key: "work", label: "Work" },
  { key: "labs", label: "Labs" },
];

export const DOMAINS = [
  {
    key: "tasks",
    label: "Tasks",
    group: "work",
    basePath: "/tasks",
    subNav: [
      { key: "board", label: "Board", path: "/tasks/board" },
      { key: "projects", label: "Projects", path: "/tasks/projects" },
      { key: "archive", label: "Archive", path: "/tasks/archive" },
      { key: "activity", label: "Activity", path: "/tasks/activity" },
    ],
  },
  // Threads with the household — people and agents (projects/discussions).
  {
    key: "discussions",
    label: "Discussions",
    group: "work",
    basePath: "/discussions",
    subNav: [
      { key: "all", label: "All", path: "/discussions" },
      { key: "new", label: "New", path: "/discussions/new" },
    ],
  },
  {
    key: "knowledge",
    label: "Library",
    group: "work",
    basePath: "/knowledge",
    subNav: [
      { key: "documents", label: "Documents", path: "/knowledge/documents" },
      { key: "tags", label: "Tags", path: "/knowledge/tags" },
    ],
  },
  {
    key: "snippets",
    label: "Snippets",
    group: "work",
    basePath: "/snippets",
    subNav: [
      { key: "list", label: "All", path: "/snippets" },
      { key: "new", label: "New", path: "/snippets/new" },
    ],
  },
  // Sketches and diagrams (Excalidraw) — private or shared with the
  // household; the editor lives at /drawings/:id.
  {
    key: "drawings",
    label: "Drawings",
    group: "work",
    basePath: "/drawings",
    subNav: [{ key: "all", label: "All", path: "/drawings" }],
  },
  {
    key: "study",
    label: "Study",
    group: "labs",
    basePath: "/study",
    subNav: [
      { key: "review", label: "Review", path: "/study/review" },
      { key: "create", label: "Create", path: "/study/create" },
      { key: "all", label: "All", path: "/study/all" },
      { key: "decks", label: "Decks", path: "/study/decks" },
    ],
  },
  // The Yates remodel planner (projects/yates-planner): the house as a 2D
  // plan and a 3D model, its as-built and the remodel plans drawn on it;
  // a plan opens at /house/plans/:planId.
  {
    key: "house",
    label: "House",
    group: "labs",
    basePath: "/house",
    subNav: [{ key: "plans", label: "Plans", path: "/house" }],
  },
  {
    key: "domain-search",
    label: "Domains",
    group: "labs",
    basePath: "/domain-search",
    subNav: [{ key: "search", label: "Search", path: "/domain-search" }],
  },
  // Strength training (projects/strength-training, D1): today's session
  // from what's recovered and behind, its history, progress (weekly sets
  // per muscle, each main lift's estimated max), your gym. A finished
  // session opens at /training/history/:id — `nested`, so History stays
  // the current tab there.
  {
    key: "training",
    label: "Training",
    group: "labs",
    basePath: "/training",
    subNav: [
      { key: "today", label: "Today", path: "/training" },
      { key: "history", label: "History", path: "/training/history", nested: true },
      { key: "progress", label: "Progress", path: "/training/progress" },
      { key: "settings", label: "Settings", path: "/training/settings" },
    ],
  },
  // Investing (projects/investing D1): the research desk — every company
  // we've looked at, its verdict and research (a company opens at
  // /investing/research/:symbol, a thesis at /investing/theses/:slug —
  // both `nested`, so their tab stays lit) — the target (what the account
  // should hold, the change open against it, the latest sync plan; anyone
  // reads it, only a person decides), the fund (the scoreboard against the
  // benchmark, its line, the positions beside the target; anyone reads
  // it), and the fund's settings, a person's only (`personOnly`: SubNav
  // leaves it out until /users/me/ says human).
  {
    key: "investing",
    label: "Investing",
    group: "labs",
    basePath: "/investing",
    subNav: [
      { key: "research", label: "Research", path: "/investing/research", nested: true },
      { key: "theses", label: "Theses", path: "/investing/theses", nested: true },
      { key: "target", label: "Target", path: "/investing/target" },
      { key: "fund", label: "Fund", path: "/investing/fund" },
      { key: "settings", label: "Settings", path: "/investing/settings", personOnly: true },
    ],
  },
  // Home videos (projects/home-videos): the household's family videos —
  // upload one, let Gemini draft its scenes, then name the people and fix
  // the draft beside the player. A video opens at /home-videos/:id (and an
  // upload at /home-videos/upload); neither lights a tab, since Library
  // can't be `nested` without also lighting on People.
  {
    key: "home-videos",
    label: "Home videos",
    group: "labs",
    basePath: "/home-videos",
    subNav: [
      { key: "library", label: "Library", path: "/home-videos" },
      // nested: a person's reel builder (/home-videos/people/:id/reel) keeps it lit
      { key: "people", label: "People", path: "/home-videos/people", nested: true },
      // projects/home-video-reels: a person's scenes as one MP4
      { key: "reels", label: "Reels", path: "/home-videos/reels" },
    ],
  },
  // Live trivia for a room (projects/trivia): start a game here and put
  // /trivia/:id on the projector (`nested`, so Host stays lit); players
  // join at /play, outside the shell. Questions live in Django admin.
  {
    key: "trivia",
    label: "Trivia",
    group: "labs",
    basePath: "/trivia",
    subNav: [{ key: "host", label: "Host", path: "/trivia", nested: true }],
  },
  // Agents are household-wide principals (programs with their own account)
  // — minted, watched, and retired here; they work the Tasks board. The
  // household itself (who's in it, invitations) is managed here too. All
  // token management lives here: agent tokens on Overview, yours on My
  // token (moved from Knowledge → Auth Token; the old URL redirects).
  {
    key: "agents",
    label: "Agents",
    group: "account",
    basePath: "/agents",
    subNav: [
      { key: "overview", label: "Overview", path: "/agents" },
      { key: "household", label: "Household", path: "/agents/household" },
      { key: "my-token", label: "My token", path: "/agents/my-token" },
    ],
  },
];

// The landing path for authenticated users (login, "/", the brand link,
// Not Found's way back). Centralized so changing it is a one-line edit.
// The task board: it is the daily starting point now; Study moved under
// Labs with the other side projects (board #104).
export const DEFAULT_ROUTE = "/tasks/board";

/** The sections in one header group ("work", "labs", "account"). */
export function domainsInGroup(group) {
  return DOMAINS.filter((domain) => domain.group === group);
}

/** The header group a section belongs to, by key. */
export function groupOf(domain) {
  return NAV_GROUPS.find((g) => g.key === domain?.group) || null;
}

/** Where a section sits in the header, in words: "Work", "Labs", or
 * "Account" (the avatar menu); null outside every section. The SubNav
 * breadcrumb and WebMCP's where_am_i both say it. */
export function groupLabel(domain) {
  if (!domain) return null;
  if (domain.group === "account") return "Account";
  return groupOf(domain)?.label ?? null;
}

// Match a pathname to its owning domain. Uses segment-boundary matching
// (not raw startsWith) so a future `/know` domain won't steal `/knowledge/*`
// and `/knowledgeable` won't match `/knowledge`.
export function findDomainByPath(pathname) {
  return (
    DOMAINS.find(
      (domain) =>
        pathname === domain.basePath ||
        pathname.startsWith(domain.basePath + "/"),
    ) || null
  );
}
